import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Typography,
    Box,
} from '@mui/material';
import theme from '../../../../theme';
import { DEFICIENCY_STATUSES, getStatusChipProps } from '../constants';

// Define the status flow based on the official statuses
const STATUS_ORDER = [
    DEFICIENCY_STATUSES.SUBMITTED,
    DEFICIENCY_STATUSES.ACCEPTED,
    DEFICIENCY_STATUSES.ASSIGNED,
    DEFICIENCY_STATUSES.WORK_COMPLETED,
    DEFICIENCY_STATUSES.INTERNALLY_SIGNED_OFF,
    DEFICIENCY_STATUSES.COMPLETED,
    DEFICIENCY_STATUSES.REJECTED,
];

/**
 * Modal for changing status of selected deficiencies
 * Allows users to select a new status for one or more deficiencies
 * Displays all available statuses with appropriate styling
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSubmit - Callback when status change is submitted
 * @param {Set<string>} props.selectedItems - Set of selected deficiency IDs
 * @returns {React.ReactElement} Status change modal dialog
 */
const StatusChangeModal = ({ open, onClose, onSubmit, selectedItems }) => {
    // State for the selected status
    const [selectedStatus, setSelectedStatus] = useState('');

    /**
     * Handle submission of the status change
     * Calls onSubmit with the selected items and new status
     */
    const handleSubmit = () => {
        // Get array of IDs from selectedItems Set
        const selectedIdsArray = Array.from(selectedItems);
        // Call onSubmit with array of IDs and selected status
        onSubmit(selectedIdsArray, selectedStatus);
        setSelectedStatus('');
        onClose();
    };

    /**
     * Handle modal close
     * Resets the selected status and calls onClose
     */
    const handleClose = () => {
        setSelectedStatus('');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    width: '100%',
                    maxWidth: '400px',
                },
            }}
        >
            <DialogTitle>
                <Typography variant="h6" fontWeight={theme.typography.fontWeights.semiBold}>
                    Change Status
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select the new status for {selectedItems?.size || 0} selected item(s)
                </Typography>

                <RadioGroup
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                >
                    {STATUS_ORDER.map((status) => {
                        const chipProps = getStatusChipProps(status.value);
                        return (
                            <FormControlLabel
                                key={status.value}
                                value={status.value}
                                control={
                                    <Radio
                                        sx={{
                                            color: theme.palette.grey[300],
                                            '&.Mui-checked': {
                                                color: status.color,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box
                                            sx={{
                                                display: 'inline-block',
                                                borderRadius: '16px',
                                                px: 1.5,
                                                py: 0.5,
                                                fontSize: theme.typography.fontSize.sm,
                                                fontWeight: theme.typography.fontWeights.medium,
                                                ...chipProps.sx,
                                            }}
                                        >
                                            {status.label}
                                        </Box>
                                    </Box>
                                }
                                sx={{
                                    py: 1,
                                    px: 1,
                                    borderRadius: theme.shape.borderRadius / 4,
                                    width: '100%',
                                    margin: 0,
                                    '&:hover': {
                                        backgroundColor: theme.palette.grey[50],
                                    },
                                }}
                            />
                        );
                    })}
                </RadioGroup>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} variant="outlined" color="inherit">
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={!selectedStatus}>
                    Update Status
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StatusChangeModal;
